import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import {
  Plus, Download, Star, Lightbulb, Loader2, KeyRound, Mail, Bell, Ban, Trash2,
  CheckCircle2, Pencil, X, StickyNote, Send, RotateCcw,
} from "lucide-react";

const money = (v) => `${Number(v || 0).toLocaleString("es-ES", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const dmy = (v) => (v ? new Date(v).toLocaleDateString("es-ES") : "—");

const STATE_STYLE = {
  sin_utilizar: "bg-slate-100 text-slate-600 border-slate-200",
  asignada: "bg-blue-100 text-blue-800 border-blue-200",
  en_uso: "bg-slate-900 text-white border-slate-900",
  inactiva: "bg-amber-100 text-amber-900 border-amber-200",
  caducada: "bg-red-100 text-red-800 border-red-200",
};

const FILTERS = [
  { key: "todas", label: "Todas" },
  { key: "sin_utilizar", label: "Sin utilizar" },
  { key: "en_uso", label: "En uso" },
  { key: "sin_facturar", label: "Sin facturar" },
  { key: "facturadas_sin_cobrar", label: "Facturadas y sin cobrar", amount: true },
  { key: "facturadas_cobradas", label: "Facturadas y cobradas", amount: true },
  { key: "inactivas", label: "Inactivas" },
];

const Kpi = ({ label, value, sub, tone = "slate", testid }) => (
  <div className="card p-5" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</div>
    <div className={`font-display text-3xl font-extrabold mt-1 ${tone === "green" ? "text-emerald-600" : tone === "amber" ? "text-amber-600" : tone === "blue" ? "text-blue-700" : ""}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function AdminLicencias() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("todas");
  const [q, setQ] = useState("");
  const [showTest, setShowTest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [modal, setModal] = useState(null); // {type, license}

  const load = async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([
        api.get("/admin/licenses", { params: { estado: filter, q, show_test: showTest } }),
        api.get("/admin/licenses/stats", { params: { show_test: showTest } }),
      ]);
      setRows(l.data);
      setStats(s.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, showTest]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const act = async (id, fn, okMsg) => {
    setBusyId(id); setError(""); setInfo("");
    try {
      const r = await fn();
      if (okMsg) setInfo(typeof okMsg === "function" ? okMsg(r?.data) : okMsg);
      await load();
      return r;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusyId(null); }
  };

  const exportExcel = async () => {
    const r = await api.get("/admin/licenses/export", { params: { show_test: showTest }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "licencias.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const counts = stats?.counts || {};
  const importes = stats?.importes || {};
  const visible = useMemo(() => rows, [rows]);

  return (
    <div className="p-8 fade-in" data-testid="admin-licencias-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Administración</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight mt-1 mb-2">Licencias</h1>
          <p className="text-slate-600 max-w-xl">Crea licencias, regenera contraseñas, corrige emails y consulta el estado de facturación y acceso de cada farmacia.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button className="btn btn-outline" onClick={() => setModal({ type: "mejoras" })} data-testid="improvements-button"><Lightbulb size={15} /> Informe de mejoras</button>
          <button className="btn btn-outline" onClick={exportExcel} data-testid="export-button"><Download size={15} /> Exportar Excel</button>
          <button className="btn btn-outline" onClick={() => setModal({ type: "resenas" })} data-testid="reviews-button"><Star size={15} /> Reseñas</button>
          <button className="btn btn-primary" onClick={() => setModal({ type: "crear" })} data-testid="new-license-button"><Plus size={15} /> Crear licencias</button>
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="licencias-error">{error}</div>}
      {info && <div className="mb-4 text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 flex items-start justify-between gap-3" data-testid="licencias-info"><span>{info}</span><button onClick={() => setInfo("")}><X size={14} /></button></div>}

      {stats && (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-4" data-testid="licencias-kpis">
            <Kpi testid="kpi-facturado" label={`Facturado ${stats.year}`} value={money(stats.facturado)} sub={`${stats.facturas} facturas · ${money(stats.estimado_tarifa)} estimados por tarifa`} />
            <Kpi testid="kpi-cobrado" label={`Cobrado ${stats.year}`} value={money(stats.cobrado)} tone="green" sub={`${stats.cobradas} licencias cobradas`} />
            <Kpi testid="kpi-pendiente" label="Pendiente de cobro" value={money(stats.pendiente)} tone="amber" sub={`${stats.sin_cobrar} facturas sin cobrar`} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Kpi testid="kpi-total" label="Total" value={stats.total} />
            <Kpi testid="kpi-disponibles" label="Disponibles" value={stats.disponibles} tone="green" />
            <Kpi testid="kpi-en-uso" label="En uso" value={stats.en_uso} tone="blue" />
            <Kpi testid="kpi-suscripciones" label="Suscripciones activas" value={stats.suscripciones_activas} />
          </div>
        </>
      )}

      <div className="card p-4 mb-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${filter === f.key ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {f.label} <span className="opacity-60 ml-1">{counts[f.key] ?? 0}</span>
              {f.amount && importes[f.key] ? <span className="opacity-60 ml-1">· {money(importes[f.key])}</span> : null}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <input className="input flex-1 min-w-[240px]" placeholder="Buscar por código, farmacia, email, provincia…"
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input" />
          <label className="text-sm text-slate-600 flex items-center gap-2">
            <input type="checkbox" checked={showTest} onChange={(e) => setShowTest(e.target.checked)} data-testid="show-test-checkbox" />
            Mostrar licencias de test
          </label>
        </div>
      </div>

      <div className="card overflow-x-auto">
        <table className="dense" data-testid="licencias-table">
          <thead>
            <tr>
              <th>Código</th><th>Estado</th><th>Farmacia · Email</th><th>Facturada</th>
              <th>Facturable</th><th>Cobro / Renovación</th><th>Provincia</th>
              <th>Alta</th><th>Primer acceso</th><th className="text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="10" className="text-center py-8 text-slate-500"><Loader2 size={16} className="animate-spin inline" /> Cargando…</td></tr>
              : visible.length === 0 ? <tr><td colSpan="10" className="text-center py-8 text-slate-500">No hay licencias con este filtro. Crea las primeras con «Crear licencias».</td></tr>
                : visible.map((l) => (
                  <tr key={l.id} data-testid={`license-row-${l.code}`}>
                    <td className="font-mono text-xs font-semibold">{l.code}{l.is_test && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">TEST</span>}</td>
                    <td><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${STATE_STYLE[l.state]}`} data-testid={`license-state-${l.code}`}>{l.state_label}</span></td>
                    <td>
                      {l.email ? (
                        <>
                          <div className="font-semibold">{l.pharmacy_name || "—"}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            {l.email}
                            <button onClick={() => setModal({ type: "email", license: l })} className="text-slate-400 hover:text-slate-700" data-testid={`edit-email-${l.code}`}><Pencil size={11} /></button>
                          </div>
                          <button onClick={() => setModal({ type: "nota", license: l })} className="text-[11px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mt-1 border border-dashed border-slate-300 rounded px-2 py-0.5" data-testid={`note-${l.code}`}>
                            <StickyNote size={11} /> {l.notes ? l.notes.slice(0, 28) : "Añadir nota"}
                          </button>
                        </>
                      ) : (
                        <button className="btn btn-outline btn-sm" onClick={() => setModal({ type: "asignar", license: l })} data-testid={`assign-${l.code}`}>
                          <Send size={13} /> Asignar farmacia
                        </button>
                      )}
                    </td>
                    <td>
                      <button onClick={() => act(l.id, () => api.post(`/admin/licenses/${l.id}/invoice`, { invoiced: !l.invoiced }))}
                        className={`px-2 py-1 rounded-full text-[11px] border ${l.invoiced ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-white text-slate-500 border-slate-300"}`}
                        data-testid={`invoice-toggle-${l.code}`}>
                        {l.invoiced ? `${money(l.invoice_amount)} · ${dmy(l.invoice_date)}` : "Sin facturar"}
                      </button>
                    </td>
                    <td>
                      <button onClick={() => act(l.id, () => api.patch(`/admin/licenses/${l.id}`, { billable: !l.billable }))}
                        className={`px-2 py-0.5 rounded-full text-[11px] border ${l.billable ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}
                        data-testid={`billable-toggle-${l.code}`}>
                        {l.billable ? "sí" : "no"}
                      </button>
                    </td>
                    <td className="text-xs">
                      {l.paid
                        ? <span className="text-emerald-700 font-semibold">Cobrada {dmy(l.paid_date)}</span>
                        : <button disabled={!l.invoiced} onClick={() => act(l.id, () => api.post(`/admin/licenses/${l.id}/paid`, { paid: true }))}
                            className="border border-dashed border-slate-300 rounded px-2 py-1 hover:border-slate-500 disabled:opacity-40" data-testid={`pay-${l.code}`}>
                            Fecha de cobro
                          </button>}
                      <div className="text-[11px] text-slate-500 mt-1">Renueva {dmy(l.renewal_date)}</div>
                    </td>
                    <td className="text-xs">
                      {l.province || "—"}
                      <button onClick={() => setModal({ type: "provincia", license: l })} className="text-slate-400 hover:text-slate-700 ml-1" data-testid={`edit-province-${l.code}`}><Pencil size={11} /></button>
                    </td>
                    <td className="font-mono text-[11px]">{dmy(l.created_at)}</td>
                    <td className="font-mono text-[11px]">{l.first_access_at ? dmy(l.first_access_at) : "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      {busyId === l.id && <Loader2 size={13} className="animate-spin inline mr-2" />}
                      <button disabled={!l.email} onClick={() => act(l.id, () => api.post(`/admin/licenses/${l.id}/send-access`), "Email de acceso enviado a la farmacia")}
                        className="btn btn-outline btn-sm mr-2 disabled:opacity-40" data-testid={`send-access-${l.code}`}><Mail size={13} /> Email cliente</button>
                      <button onClick={() => setModal({ type: "resena", license: l })} className={`p-1 ${l.review ? "text-amber-500" : "text-slate-400 hover:text-amber-500"}`} title="Reseña" data-testid={`review-${l.code}`}><Star size={14} fill={l.review ? "currentColor" : "none"} /></button>
                      <button disabled={!l.email} onClick={() => act(l.id, () => api.post(`/admin/licenses/${l.id}/regenerate-password`), (d) => `Nueva contraseña de ${d.email}: ${d.password}`)}
                        className="p-1 text-slate-400 hover:text-slate-900 disabled:opacity-30" title="Regenerar contraseña" data-testid={`regen-${l.code}`}><KeyRound size={14} /></button>
                      <button disabled={!l.invoiced || l.paid} onClick={() => act(l.id, () => api.post(`/admin/licenses/${l.id}/send-reminder`), "Recordatorio de cobro enviado")}
                        className="p-1 text-slate-400 hover:text-amber-600 disabled:opacity-30" title="Recordar cobro" data-testid={`reminder-${l.code}`}><Bell size={14} /></button>
                      <button onClick={() => act(l.id, () => api.post(`/admin/licenses/${l.id}/renew`, { months: 12 }), "Licencia renovada 12 meses")}
                        className="p-1 text-slate-400 hover:text-emerald-700" title="Renovar 12 meses" data-testid={`renew-${l.code}`}><RotateCcw size={14} /></button>
                      <button onClick={() => act(l.id, () => api.post(`/admin/licenses/${l.id}/active/${!l.active}`))}
                        className={`p-1 ${l.active ? "text-slate-400 hover:text-red-600" : "text-emerald-600"}`} title={l.active ? "Desactivar" : "Activar"} data-testid={`toggle-active-${l.code}`}>
                        {l.active ? <Ban size={14} /> : <CheckCircle2 size={14} />}
                      </button>
                      <button onClick={() => { if (window.confirm(`¿Eliminar la licencia ${l.code}?`)) act(l.id, () => api.delete(`/admin/licenses/${l.id}`)); }}
                        className="p-1 text-slate-400 hover:text-red-700" title="Eliminar" data-testid={`delete-${l.code}`}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {modal && <Modals modal={modal} close={() => setModal(null)} reload={load} setInfo={setInfo} setError={setError} />}
    </div>
  );
}

function Modal({ title, children, close, testid }) {
  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid={testid}>
      <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">{title}</h3>
          <button onClick={close} className="text-slate-400 hover:text-slate-900" data-testid="modal-close"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Modals({ modal, close, reload, setInfo, setError }) {
  const l = modal.license;
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    count: 5, tariff_eur: 300, months: 12, is_test: false,
    pharmacy_name: "", email: l?.email || "", contact_name: "", province: l?.province || "",
    city: "", phone: "", send_email: true, notes: l?.notes || "",
    rating: l?.review?.rating || 5, text: l?.review?.text || "",
    title: "", detail: "",
  });
  const [list, setList] = useState(null);

  useEffect(() => {
    if (modal.type === "resenas") api.get("/admin/reviews").then((r) => setList(r.data));
    if (modal.type === "mejoras") api.get("/admin/improvements").then((r) => setList(r.data));
  }, [modal.type]);

  const run = async (fn, msg) => {
    setBusy(true);
    setError("");
    setInfo("");
    try {
      await fn();
      if (msg) setInfo(msg);
      await reload();
      close();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      close();
    } finally { setBusy(false); }
  };

  if (modal.type === "crear") return (
    <Modal title="Crear licencias" close={close} testid="create-licenses-modal">
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Se crean con código correlativo y quedan como «sin utilizar» hasta que las asignes a una farmacia.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Cuántas
            <input type="number" min="1" max="200" className="input mt-1" value={form.count} onChange={(e) => setForm({ ...form, count: e.target.value })} data-testid="bulk-count-input" />
          </label>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Tarifa anual (€)
            <input type="number" step="1" className="input mt-1" value={form.tariff_eur} onChange={(e) => setForm({ ...form, tariff_eur: e.target.value })} data-testid="bulk-tariff-input" />
          </label>
          <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Duración (meses)
            <input type="number" min="1" className="input mt-1" value={form.months} onChange={(e) => setForm({ ...form, months: e.target.value })} data-testid="bulk-months-input" />
          </label>
          <label className="text-sm text-slate-600 flex items-end gap-2 pb-2">
            <input type="checkbox" checked={form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} data-testid="bulk-test-checkbox" /> Son de test
          </label>
        </div>
        <button disabled={busy} className="btn btn-primary w-full" data-testid="bulk-submit"
          onClick={() => run(() => api.post("/admin/licenses/bulk", {
            count: Number(form.count), tariff_eur: Number(form.tariff_eur),
            months: Number(form.months), is_test: form.is_test,
          }), `${form.count} licencia(s) creadas`)}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Crear
        </button>
      </div>
    </Modal>
  );

  if (modal.type === "asignar") return (
    <Modal title={`Asignar ${l.code} a una farmacia`} close={close} testid="assign-modal">
      <div className="space-y-3">
        <input className="input" placeholder="Nombre de la farmacia *" value={form.pharmacy_name} onChange={(e) => setForm({ ...form, pharmacy_name: e.target.value })} data-testid="assign-pharmacy-input" />
        <input className="input" type="email" placeholder="Email de la farmacia *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="assign-email-input" />
        <input className="input" placeholder="Titular" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="assign-contact-input" />
        <div className="grid grid-cols-2 gap-3">
          <input className="input" placeholder="Provincia" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} data-testid="assign-province-input" />
          <input className="input" placeholder="Población" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="assign-city-input" />
        </div>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          <input type="checkbox" checked={form.send_email} onChange={(e) => setForm({ ...form, send_email: e.target.checked })} data-testid="assign-send-email" />
          Enviarle ya el email con su licencia y el enlace para crear la contraseña
        </label>
        <button disabled={busy || !form.pharmacy_name || !form.email} className="btn btn-primary w-full" data-testid="assign-submit"
          onClick={() => run(() => api.post(`/admin/licenses/${l.id}/assign`, {
            pharmacy_name: form.pharmacy_name, email: form.email, contact_name: form.contact_name || undefined,
            province: form.province || undefined, city: form.city || undefined,
            send_email: form.send_email,
          }), form.send_email ? "Licencia asignada y email enviado" : "Licencia asignada")}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Asignar
        </button>
      </div>
    </Modal>
  );

  if (modal.type === "email" || modal.type === "provincia" || modal.type === "nota") {
    const field = modal.type === "email" ? "email" : modal.type === "provincia" ? "province" : "notes";
    const value = field === "email" ? form.email : field === "province" ? form.province : form.notes;
    const titles = { email: "Corregir email", provincia: "Provincia", nota: "Nota de la licencia" };
    return (
      <Modal title={`${titles[modal.type]} · ${l.code}`} close={close} testid={`edit-${modal.type}-modal`}>
        <div className="space-y-3">
          {field === "notes"
            ? <textarea className="input h-28" value={value} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="edit-field-input" />
            : <input className="input" value={value} onChange={(e) => setForm({ ...form, [field === "email" ? "email" : "province"]: e.target.value })} data-testid="edit-field-input" />}
          {field === "email" && <p className="text-xs text-slate-500">También se actualiza el email con el que accede la farmacia.</p>}
          <button disabled={busy} className="btn btn-primary w-full" data-testid="edit-field-submit"
            onClick={() => run(() => api.patch(`/admin/licenses/${l.id}`, { [field]: value }), "Guardado")}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Guardar
          </button>
        </div>
      </Modal>
    );
  }

  if (modal.type === "resena") return (
    <Modal title={`Reseña · ${l.code}`} close={close} testid="review-modal">
      <div className="space-y-3">
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} onClick={() => setForm({ ...form, rating: n })} className={n <= form.rating ? "text-amber-500" : "text-slate-300"} data-testid={`star-${n}`}>
              <Star size={24} fill={n <= form.rating ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
        <textarea className="input h-28" placeholder="Qué ha comentado la farmacia" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="review-text-input" />
        <div className="flex gap-2">
          <button disabled={busy} className="btn btn-primary" data-testid="review-submit"
            onClick={() => run(() => api.post(`/admin/licenses/${l.id}/review`, { rating: Number(form.rating), text: form.text }), "Reseña guardada")}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Star size={15} />} Guardar
          </button>
          {l.review && <button className="btn btn-outline" onClick={() => run(() => api.delete(`/admin/licenses/${l.id}/review`), "Reseña borrada")}>Borrar</button>}
        </div>
      </div>
    </Modal>
  );

  if (modal.type === "resenas") return (
    <Modal title="Reseñas de clientes" close={close} testid="reviews-modal">
      {!list ? <Loader2 size={16} className="animate-spin" /> : (
        <div className="space-y-3">
          <div className="text-sm text-slate-600">{list.count} reseña(s){list.average ? ` · media ${list.average}/5` : ""}</div>
          {list.reviews.map((r) => (
            <div key={r.code} className="border border-slate-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{r.pharmacy_name || r.code}</span>
                <span className="text-amber-500 text-sm">{"★".repeat(r.review.rating)}<span className="text-slate-300">{"★".repeat(5 - r.review.rating)}</span></span>
              </div>
              {r.review.text && <p className="text-sm text-slate-600 mt-1">{r.review.text}</p>}
              <div className="text-[11px] text-slate-400 mt-1 font-mono">{r.code} · {dmy(r.review.at)}</div>
            </div>
          ))}
          {list.count === 0 && <p className="text-sm text-slate-500">Aún no hay reseñas. Puedes anotarlas con la estrella de cada licencia.</p>}
        </div>
      )}
    </Modal>
  );

  if (modal.type === "mejoras") return (
    <Modal title="Informe de mejoras" close={close} testid="improvements-modal">
      <div className="space-y-3">
        <div className="border border-slate-200 rounded-lg p-3 space-y-2">
          <input className="input" placeholder="Título de la mejora" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="improvement-title-input" />
          <textarea className="input h-20" placeholder="Detalle / quién lo pide" value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} data-testid="improvement-detail-input" />
          <button disabled={busy || !form.title} className="btn btn-primary btn-sm" data-testid="improvement-submit"
            onClick={async () => {
              setBusy(true);
              try {
                await api.post("/admin/improvements", { title: form.title, detail: form.detail });
                const r = await api.get("/admin/improvements");
                setList(r.data); setForm({ ...form, title: "", detail: "" });
              } catch (e) {
                setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
              } finally { setBusy(false); }
            }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Añadir
          </button>
        </div>
        {!list ? <Loader2 size={16} className="animate-spin" /> : list.length === 0
          ? <p className="text-sm text-slate-500">Sin mejoras registradas. Las que manden las farmacias desde la app aparecerán aquí.</p>
          : list.map((im) => (
            <div key={im.id} className="border border-slate-200 rounded-lg p-3" data-testid={`improvement-${im.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-sm">{im.title}</div>
                  {im.detail && <p className="text-sm text-slate-600">{im.detail}</p>}
                  <div className="text-[11px] text-slate-400 mt-1">{im.pharmacy_name || "Interno"} · {dmy(im.created_at)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <select className="input text-xs py-1" value={im.status} data-testid={`improvement-status-${im.id}`}
                    onChange={async (e) => {
                      try {
                        await api.patch(`/admin/improvements/${im.id}`, { status: e.target.value });
                        const r = await api.get("/admin/improvements"); setList(r.data);
                      } catch (err) {
                        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                      }
                    }}>
                    <option value="nueva">Nueva</option>
                    <option value="en_curso">En curso</option>
                    <option value="hecha">Hecha</option>
                    <option value="descartada">Descartada</option>
                  </select>
                  <button className="text-slate-400 hover:text-red-600" data-testid={`improvement-delete-${im.id}`}
                    onClick={async () => {
                      try {
                        await api.delete(`/admin/improvements/${im.id}`);
                        const r = await api.get("/admin/improvements"); setList(r.data);
                      } catch (err) {
                        setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                      }
                    }}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
      </div>
    </Modal>
  );

  return null;
}
